import { ConfigurationContext } from "@ui/context/DocumentConfigurationContext";
import {
  useDocumentConfigurations,
  useFeatureFlags,
} from "@ui/hooks/useDocument";
import { ReactNode, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

function ConfigurationProvider(props: { children: ReactNode }) {
  const [isOnline, setOnline] = useState(navigator.onLine);
  const [isServerReachable, setServerReachable] = useState(true);

  const configurations = useDocumentConfigurations();
  const flags = useFeatureFlags();

  useEffect(() => {
    Sentry.setTag("module", flags.data?.module ?? "");
  }, [flags.data?.module]);

  const get = (type: string) => {
    return configurations.data?.[type.toLowerCase()];
  };

  const types = (type: string) => {
    const types: { name: string; label: string; color: string }[] = [];

    for (const key in configurations.data) {
      if (key.startsWith(type.toLocaleLowerCase() + "/"))
        types.push({
          name: configurations.data[key].name,
          label: configurations.data[key].label,
          color: configurations.data[key].color,
        });
    }

    return types;
  };

  const checkServerReachability = async () => {
    try {
      const response = await fetch("/api/health-check");
      setServerReachable(response.ok);
    } catch (error) {
      setServerReachable(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const interval = setInterval(checkServerReachability, 5000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  return (
    <ConfigurationContext.Provider
      value={{
        get,
        types,
        featureFlags: flags.data || {},
        isOnline,
        isServerReachable,
        isOfflineMode: !isOnline, //|| !isServerReachable,
      }}
    >
      {props.children}
    </ConfigurationContext.Provider>
  );
}

export default ConfigurationProvider;
